import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CssBaseline, GlobalStyles } from "@mui/material";

import { createAppTheme } from "./createAppTheme";

// ==============================|| THEME CONTEXT ||============================== //
const ThemeContext = createContext();

/**
 * Custom hook to access theme context
 * @returns {Object} Theme context with mode and toggleColorMode
 * @throws {Error} If used outside of CustomThemeProvider
 */
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(
      "useThemeContext must be used within a CustomThemeProvider"
    );
  }
  return context;
};

/**
 * Custom Theme Provider Component
 *
 * Provides theme context and Material-UI theme with dark/light mode support.
 * Includes localStorage persistence and system preference detection.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Theme provider component
 */
export const CustomThemeProvider = ({ children }) => {
  // ==============================|| THEME STATE ||============================== //
  const [mode, setMode] = useState(() => {
    try {
      // Check localStorage for saved preference
      const savedMode = localStorage.getItem("theme-mode");
      if (savedMode && ["light", "dark"].includes(savedMode)) {
        return savedMode;
      }

      // Fallback to system preference
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        return "dark";
      }

      return "light";
    } catch (error) {
      console.warn("Failed to load theme preference:", error);
      return "light";
    }
  });

  // ==============================|| THEME MODE MANAGEMENT ||============================== //
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          try {
            localStorage.setItem("theme-mode", newMode);
          } catch (error) {
            console.warn("Failed to save theme preference:", error);
          }
          return newMode;
        });
      },
      mode,
      setMode: (newMode) => {
        if (["light", "dark"].includes(newMode)) {
          setMode(newMode);
          try {
            localStorage.setItem("theme-mode", newMode);
          } catch (error) {
            console.warn("Failed to save theme preference:", error);
          }
        }
      },
    }),
    [mode]
  );

  // ==============================|| SYSTEM PREFERENCE LISTENER ||============================== //
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      // Only auto-switch if no manual preference is saved
      const savedMode = localStorage.getItem("theme-mode");
      if (!savedMode) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // ==============================|| THEME CREATION ||============================== //
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // ==============================|| RENDER ||============================== //
  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: {
              fontSize: "18px", // Base font size - scales all rem units proportionally
            },
            body: {
              backgroundColor: theme.palette.background.default,
              transition: "background-color 0.3s ease",
            },
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.grey[400]} transparent`,
            },
            "*::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "*::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
